<!--
  旋转特征属性栏组件。

  文件定位：
  - 该组件只负责把旋转凸台/旋转切除共用的状态映射为属性面板 UI；
  - 几何预览、提交、选择解析均通过 PropertyManagerContext 回调进入 cad-core；
  - 选择输入统一使用 PuppyInputSelect，避免每个特征重复拼装局部选择框。
-->
<template>
  <div class="revolve-property" data-testid="revolve-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <PuppyCollapseItem title="旋转轴" name="axis">
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">轴</span>
            </template>
            <PuppyInputSelect
              :items="axisInputItems"
              :active="axisPickerActive"
              :active-index="axisActiveIndex"
              :selected-indices="axisSelectedIndices"
              :visible-rows="2"
              :disabled="!selectionEditable"
              item-key-field="id"
              item-label-field="label"
              empty-text="请选择旋转轴"
              delete-label="清除"
              delete-all-label="清除"
              @update:active="handleAxisInputActiveUpdate"
              @delete-item="clearSelectedAxis"
              @delete-all="clearSelectedAxis"
            >
              <template #item="{ item }">
                <div
                  class="selection-entry"
                  @mouseenter="highlightAxis(true)"
                  @mouseleave="highlightAxis(false)"
                >
                  <span class="selection-marker" aria-hidden="true"></span>
                  <span class="selection-label">{{ (item as RevolveAxisSelectionItem).label }}</span>
                </div>
              </template>
            </PuppyInputSelect>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="方向 1" name="direction1">
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">条件</span>
            </template>
            <n-select
              :value="directionMode"
              size="small"
              :options="directionModeOptions"
              @update:value="updateDirectionMode"
            />
          </PuppyFormItem>

          <PuppyFormItem>
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              :checked="directionReversed"
              @update:checked="handleDirection1ReverseUpdate"
            >
              反向
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">角度</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="direction1AngleDeg"
                size="small"
                :show-button="false"
                :precision="3"
                :step="1"
                :min="0.001"
                :max="360"
                @update:value="updateDirection1Angle"
              />
              <n-input-group-label size="small">度</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem
        v-if="showDirection2"
        title="方向 2"
        name="direction2"
        checkbox
        :check-value="direction2Enabled"
        @update:check-value="updateDirection2Enabled"
      >
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">条件</span>
            </template>
            <n-select
              :value="direction2Mode"
              size="small"
              :options="direction2ModeOptions"
              :disabled="!direction2Enabled"
              @update:value="updateDirection2Mode"
            />
          </PuppyFormItem>

          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">角度</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="direction2AngleDeg"
                size="small"
                :show-button="false"
                :precision="3"
                :step="1"
                :min="0"
                :max="360"
                :disabled="!direction2Enabled"
                @update:value="updateDirection2Angle"
              />
              <n-input-group-label size="small">度</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="所选轮廓" name="contour">
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">轮廓</span>
            </template>
            <PuppyInputSelect
              :items="selectedContours"
              :active="profilePickerActive"
              :active-index="activeContourIndex"
              :selected-indices="selectedContourIndices"
              :visible-rows="3"
              :disabled="!selectionEditable"
              selection-behavior="ctrl-multiple"
              item-key-field="id"
              item-label-field="label"
              empty-text="请选择闭合轮廓"
              delete-label="移除"
              delete-all-label="清空"
              @update:active="handleProfileInputActiveUpdate"
              @update:active-index="handleActiveContourIndexUpdate"
              @update:selected-indices="handleSelectedContourIndicesUpdate"
              @item-click="handleContourClick"
              @delete-item="handleDeleteContour"
              @delete-all="clearSelectedContours"
            >
              <template #item="{ item }">
                <div
                  class="selection-entry"
                  @mouseenter="highlightContour((item as RevolveSelectedContourItem).id)"
                  @mouseleave="highlightContour(null)"
                >
                  <span class="selection-marker" aria-hidden="true"></span>
                  <span class="selection-label">{{ (item as RevolveSelectedContourItem).label }}</span>
                </div>
              </template>
            </PuppyInputSelect>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
/**
 * 旋转属性栏逻辑层。
 *
 * 设计说明：
 * - 视图层只保留纯 UI 状态，例如当前列表激活项，不持有 three.js 对象；
 * - 选择、预览、提交都通过 context 的显式回调完成，避免组件直接依赖 cad-core 具体实现；
 * - 旋转凸台和旋转切除共用该组件，旋转切除通过上层投影 context 来复用同一套 UI。
 */
import { computed, ref, watch } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NInputGroup,
  NInputGroupLabel,
  NInputNumber,
  NSelect,
} from 'naive-ui';
import type {
  PropertyManagerContext,
  RevolveSelectedContourItem,
} from '@/core';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';

interface RevolvePropertySectionProps {
  /** PropertyManager 上下文。 */
  context: PropertyManagerContext;
}

interface RevolveAxisSelectionItem {
  /** 旋转轴列表项稳定 ID。 */
  id: string;
  /** 旋转轴展示名称。 */
  label: string;
}

type RevolveDirectionMode = 'given-depth' | 'symmetric' | 'next' | 'up-to-face' | 'offset-from-face';
type RevolveDirection2Mode = 'given-depth' | 'up-to-vertex' | 'up-to-face' | 'offset-from-face';

const props = defineProps<RevolvePropertySectionProps>();

const defaultExpanded = ['axis', 'direction1', 'direction2', 'contour'];
const directionMode = ref<RevolveDirectionMode>('given-depth');
const direction2Mode = ref<RevolveDirection2Mode>('given-depth');
const activeContourIndex = ref(-1);
const selectedContourIndices = ref<number[]>([]);

const directionModeOptions = [
  { label: '给定角度', value: 'given-depth' as const },
  { label: '两侧对称', value: 'symmetric' as const, disabled: true },
  { label: '旋转到下一面', value: 'next' as const, disabled: true },
  { label: '成形到面', value: 'up-to-face' as const, disabled: true },
  { label: '到离指定面的距离', value: 'offset-from-face' as const, disabled: true },
];

const direction2ModeOptions = [
  { label: '给定角度', value: 'given-depth' as const },
  { label: '成形到顶点', value: 'up-to-vertex' as const, disabled: true },
  { label: '成形到面', value: 'up-to-face' as const, disabled: true },
  { label: '到离指定面的距离', value: 'offset-from-face' as const, disabled: true },
];

const selectionEditable = computed(() => !(props.context.revolveState as { editingFeatureId?: string | null } | null | undefined)?.editingFeatureId);
const selectedContours = computed(() => props.context.revolveSelectedContours ?? []);
const selectedAxisLabel = computed(() => props.context.revolveSelectedAxisLabel ?? null);
const directionReversed = computed(() => props.context.revolveDirectionReversed === true);
const direction2Enabled = computed(() => props.context.revolveDirection2Enabled === true);
const showDirection2 = computed(() => directionMode.value !== 'symmetric');
const axisPickerActive = computed(() => props.context.revolvePickerTarget === 'axis');
const profilePickerActive = computed(() => props.context.revolvePickerTarget === 'profile');
const axisInputItems = computed<RevolveAxisSelectionItem[]>(() => {
  if (!selectedAxisLabel.value) {
    return [];
  }
  return [{
    id: 'selected-revolve-axis',
    label: selectedAxisLabel.value,
  }];
});
const axisActiveIndex = computed(() => (axisInputItems.value.length > 0 ? 0 : -1));
const axisSelectedIndices = computed(() => (axisInputItems.value.length > 0 ? [0] : []));

const direction1AngleDeg = computed(() => {
  const value = props.context.revolveDirection1Angle;
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 360;
  }
  return Number(((value * 180) / Math.PI).toFixed(3));
});

const direction2AngleDeg = computed(() => {
  const value = props.context.revolveDirection2Angle;
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Number(((value * 180) / Math.PI).toFixed(3));
});

/**
 * 角度转弧度。
 *
 * @param value 角度值。
 * @returns 弧度值。
 */
function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

/**
 * 更新方向 1 角度。
 *
 * @param value 新角度值（度）。
 */
function updateDirection1Angle(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  props.context.setRevolveDirection1Angle?.(toRadians(Math.max(0.001, value)));
}

/**
 * 更新方向 2 启用状态。
 *
 * @param value 是否启用方向 2。
 */
function updateDirection2Enabled(value: boolean): void {
  props.context.setRevolveDirection2Enabled?.(value);
}

/**
 * 更新方向 1 反向状态。
 *
 * @param checked 是否反向。
 */
function handleDirection1ReverseUpdate(checked: boolean): void {
  props.context.setRevolveDirectionReversed?.(checked);
}

/**
 * 更新方向模式。
 *
 * @param value 新模式。
 */
function updateDirectionMode(value: RevolveDirectionMode | null): void {
  if (!value) {
    return;
  }
  directionMode.value = value;
}

/**
 * 更新方向 2 模式。
 *
 * @param value 新模式。
 */
function updateDirection2Mode(value: RevolveDirection2Mode | null): void {
  if (!value) {
    return;
  }
  direction2Mode.value = value;
}

/**
 * 更新方向 2 角度。
 *
 * @param value 新角度值（度）。
 */
function updateDirection2Angle(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  props.context.setRevolveDirection2Angle?.(toRadians(Math.max(0, value)));
}

/**
 * 激活旋转选择框。
 *
 * @param target 选择目标。
 */
function activatePicker(target: 'axis' | 'profile'): void {
  props.context.activateRevolvePicker?.(target);
}

/**
 * 同步旋转轴输入框激活态。
 *
 * @param active 输入框是否被激活。
 */
function handleAxisInputActiveUpdate(active: boolean): void {
  if (!active) {
    return;
  }
  activatePicker('axis');
}

/**
 * 同步轮廓输入框激活态。
 *
 * @param active 输入框是否被激活。
 */
function handleProfileInputActiveUpdate(active: boolean): void {
  if (!active) {
    return;
  }
  activatePicker('profile');
}

/**
 * 更新轮廓列表当前激活项。
 *
 * @param index 激活项索引。
 */
function handleActiveContourIndexUpdate(index: number): void {
  activeContourIndex.value = index;
}

/**
 * 更新轮廓列表选择索引。
 *
 * @param indices 选择索引集合。
 */
function handleSelectedContourIndicesUpdate(indices: number[]): void {
  selectedContourIndices.value = indices;
}

/**
 * 处理轮廓条目点击。
 *
 * @param payload 条目点击载荷。
 */
function handleContourClick(payload: { item: unknown }): void {
  const item = payload.item as RevolveSelectedContourItem;
  highlightContour(item.id);
}

/**
 * 处理轮廓条目删除。
 *
 * @param payload 条目删除载荷。
 */
function handleDeleteContour(payload: { item: unknown }): void {
  const item = payload.item as RevolveSelectedContourItem;
  removeContour(item.id);
}

/**
 * 清空已选旋转轴。
 */
function clearSelectedAxis(): void {
  props.context.clearRevolveSelectedAxis?.();
}

/**
 * 清空已选轮廓。
 */
function clearSelectedContours(): void {
  props.context.clearRevolveSelectedContours?.();
}

/**
 * 移除已选轮廓。
 *
 * @param id 轮廓 ID。
 */
function removeContour(id: string): void {
  props.context.removeRevolveSelectedContour?.(id);
}

/**
 * 高亮轮廓。
 *
 * @param id 轮廓 ID。
 */
function highlightContour(id: string | null): void {
  props.context.highlightRevolveSelectedContour?.(id);
}

/**
 * 高亮旋转轴。
 *
 * @param enabled 是否高亮。
 */
function highlightAxis(enabled: boolean): void {
  props.context.highlightRevolveSelectedAxis?.(enabled);
}

watch(
  () => directionMode.value,
  (mode) => {
    if (mode !== 'symmetric') {
      return;
    }
    // 对称旋转暂未开放，若后续开放该模式，方向 2 仍由这里统一关闭，避免总角度超过 360 度。
    props.context.setRevolveDirection2Enabled?.(false);
  },
);

watch(selectedContours, (items) => {
  if (items.length === 0) {
    activeContourIndex.value = -1;
    selectedContourIndices.value = [];
    return;
  }
  if (activeContourIndex.value >= items.length) {
    activeContourIndex.value = items.length - 1;
  }
  selectedContourIndices.value = selectedContourIndices.value.filter((index) => index >= 0 && index < items.length);
});
</script>

<style scoped>
.revolve-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.88);
}

.field-label-empty {
  display: inline-flex;
  width: 32px;
  min-width: 32px;
  height: 20px;
}

.selection-entry {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.selection-marker {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #8fb8ff;
}

.selection-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
